import '@okki/fix-date'
import './plugins/assets-retry'
import { registerServiceWorker, unregisterServiceWorker } from './register-service-worker'
import i18n from '@/locales'
import './assets/fonts/iconfont.css'
import mitt from 'mitt'
import Vue from 'vue'
import Dexie from 'dexie'
import cookie from 'js-cookie'
import CODE from '@shared/constants/code'
import { jumpToUrl } from '@/utils/jump-to-url/index'
import { initSentry } from '@/utils/sentry'
import { initResouceErrorLisenter } from '@/utils/resource-error'
import { isDesktopOpen } from '@shared/utils/desktop-view'

import { RegistrableApp, registerMicroApps, render, setDefaultMountApp, start } from 'qiankun'

import { AppProps, getAppEntry, getLifeCycles, getSubAppsConfig } from './sub-apps'
import Framework from './Framework.vue'
import VueCompositionApi from '@vue/composition-api'

import { ReplaySubject, from, interval } from 'rxjs'
import { first, switchMap } from 'rxjs/operators'

import bootstrapSocket from './plugins/notice'
import emitter from './utils/emitter'
import { setupThirdPartyStatistics, sensorsData } from '@/utils/third-party-statistics'
import { checkNewVersion } from '@/utils/check-version'
import { pageUser } from '@/api/page'
import { HOSTS, RUNTIME_ENV, ASSETS } from '@/constants/environment'

import { setMountMaxTime } from 'single-spa'
import { tabOnly } from '@/utils/tab-only'
import { shellConfig } from './shell-config'
import { getLocalTimezone } from './utils'
import { saveUserSetting, fetchLoginLimit } from './api/user'
import { requestPermission } from '@shared/utils/notification'

import tabtalk from '@shared/utils/tabtalk'
import {
  sourceFetch,
  createRepeatFetch,
  toMainDomain,
  reportRetry,
  addRetrySentryBreadcrumb,
} from './utils/retry-fetch'
import { dialog } from 'amumu-ui/src/factory'

import Antd from './plugins/ant-design-vue'
import './assets/styles/base.scss'

const $mitt = mitt()
window.$mitt = $mitt

Vue.use(Antd)

setMountMaxTime(30000, false)

let sw: ReturnType<typeof registerServiceWorker>

if (RUNTIME_ENV !== 'production') {
  unregisterServiceWorker()
}

// test only start
// @ts-ignore
window.sw = sw
// @ts-ignore
window.unregisterServiceWorker = unregisterServiceWorker
// test only end

window.masterEvent = emitter

Vue.use(VueCompositionApi)

Vue.config.devtools = RUNTIME_ENV !== 'production'

emitter.$on('jumpToUrl', (url: string) => {
  jumpToUrl(url)
})

if (shellConfig.initSocket && !isDesktopOpen) {
  bootstrapSocket(shellConfig.mode)
}

if (['production', 'omg'].includes(RUNTIME_ENV)) {
  initSentry('crm_shell', process.env.CRM_SHELL_RELEASE)
  initResouceErrorLisenter()
}

const getLoginLink = () => `${HOSTS.LOGIN}/login?system_id=v5client&return_url=${encodeURIComponent(location.href)}`

const crmForbiddenUrl = encodeURIComponent(`${HOSTS.LOGIN}/login?system_id=v5client&return_url=${location.href}`)

emitter.$on('http.code.exception', (code: number) => {
  const pathPrefix = shellConfig.defaultMountApp
  switch (Number(code)) {
    case CODE.LEADS_LOGIN_LIMIT:
      dialog.closeAll()
      dialog
        .alert({
          closable: false,
          title: '提示',
          message: '当前账号已在其他设备登录，请重新登录并确认账号安全',
        })
        .then(() => {
          fetchLoginLimit().then((data: any) => {
            if (data.code === 0) {
              window.location.reload()
              return
            }
          })
        })
      break
    case CODE.NO_LOGIN:
      location.href = getLoginLink()
      break
    case CODE.CRM_FORBIDDEN:
      window.location.replace(`/page/login-xiaoman.html?t=${crmForbiddenUrl}`)
      break
    case CODE.EXPIRE:
    case CODE.EXCESS:
    case CODE.DISABLE:
    case CODE.NO_PRIVILEGE:
      jumpToUrl(`${pathPrefix}401?code=${code}`)
      break

    case CODE.NEED_PURCHASE:
      window.location.replace(HOSTS.MALL)
      break

    case CODE.COMPANY_NOT_MINE:
    case CODE.INVOICE_NOT_PRIVILEGE_VIEW:
    case CODE.INVOICE_NOT_PRIVILEGE_MANAGE:
    case CODE.EDM_NOT_PRIVILEGE_VIEW:
      jumpToUrl(`${pathPrefix}exception/privilege?code=${code}`)
      break

    case CODE.COMPANY_RECYCLE:
    case CODE.COMPANY_DELETE:
    case CODE.INVOICE_NOT_RECYCLE:
    case CODE.INVOICE_NOT_DELETE:
    case CODE.PRODUCT_NOT_RECYCLE:
    case CODE.PRODUCT_NOT_DELETE:
    case CODE.CASH_COLLECTIONS_NOT_RECYCLE:
    case CODE.CASH_COLLECTIONS_NOT_DELETE:
      jumpToUrl(`${pathPrefix}exception/delete?code=${code}`)
      break

    case CODE.AI_NOT_PRIVILEGE_VIEW:
      jumpToUrl(`${pathPrefix}ai/exception/privilege?code=${code}`)
      break

    case CODE.ONLY_DISCOVERY_CODE:
      window.location.replace(HOSTS.DX)
      break

    case CODE.SYSTEM_UPGRADE:
      jumpToUrl(`${pathPrefix}page/upgrade.html`)
      break

    default:
      break
  }
})

const pageUserSubject = new ReplaySubject<any>(1)
const pageUser$ = from(pageUser())

const updateUserInfoState = () => {
  const pageUser$ = from(pageUser()).pipe(first())
  pageUser$.subscribe((res) => {
    pageUserSubject.next(res)

    db.table('pageUser')
      .put({ id: res.data.user_id, data: res })
      .catch((err) => {
        console.log('db write failed', err)
      })
  })
  return pageUser$
}

window.masterEvent.$on('update-user-info-state', (afterUpdateEserDataEmit: string) => {
  updateUserInfoState().subscribe((res) => {
    // broadcast to other tabs
    tabtalk.$broadcast('update-user-info-state', res)

    if (afterUpdateEserDataEmit === 'after-update-user-data-emit') {
      window.masterEvent.$emit('after-update-user-data', res)
    }
  })
})

window.$mitt.on('http.code.exception', (code: number) => {
  emitter.$emit('http.code.exception', code)
})

// 注册全局更新userInfo事件，方便其他项目使用
window.$mitt.on('update-user-info', (callback: Function) => {
  updateUserInfoState().subscribe((res) => {
    // broadcast to other tabs
    tabtalk.$broadcast('update-user-info-state', res)
    callback()
  })
})

window.masterEvent.$on('clearNewMKTLiveChatMessage', () => {
  tabtalk.$emit('clearNewMKTLiveChatMessage')
})

tabOnly('timerFetchUser', () => {
  console.info('[tabOnly-timerFetchUser]: check bootstrap.')
  // timer fetch userInfoState data
  interval(60 * 60 * 1000)
    .pipe(switchMap(() => updateUserInfoState()))
    .subscribe((res) => {
      tabtalk.$broadcast('update-user-info-state', res)
    })
})

// receive other tabs
tabtalk.$on('update-user-info-state', (res: any) => {
  pageUserSubject.next(res)
})

let masterApp = new Vue({
  name: 'Shell',
  i18n,
  data() {
    return {
      content: '<div id="app"></div>',
      loading: false,
      userInfoState: {
        privilege: {
          version: null,
          user: {},
          list: [],
          scope_type: 0,
        },
        setting: {},
      },
    }
  },
  mounted() {
    window.performance.mark('vital_mark_shell')
  },
  render(h) {
    return h(Framework, {
      props: {
        content: (this as any).content,
        loading: (this as any).loading,
        userInfoState: (this as any).userInfoState,
      },
    })
  },
})
const masterAppMounted = new Promise((resolve) => {
  masterApp.$once('hook:mounted', resolve)
})

const mountSubscription = pageUserSubject.pipe(first()).subscribe(({ data }) => {
  masterApp.$mount('#container')

  // gc
  mountSubscription.unsubscribe()
})

pageUserSubject.subscribe(({ data }) => {
  masterApp.$data.userInfoState = data
})

const db = new Dexie('crm')
db.version(1).stores({
  pageUser: 'id, data',
})

pageUser$.subscribe(
  (res) => {
    if (res.code !== 0) {
      return
    }
    if (['omg', 'production'].includes(RUNTIME_ENV)) {
      // setup third party statistics
      try {
        setupThirdPartyStatistics()
        // eslint-disable-next-line
      } catch (err) {}
    }

    sensorsData()

    if ((res.data.setting.time_zone ?? false) === false) {
      const localTimezone = getLocalTimezone()
      res.data.setting.time_zone = localTimezone
      saveUserSetting({
        key: 'time_zone',
        value: localTimezone,
      })
    }
    pageUserSubject.next(res)

    db.table('pageUser')
      .put({ id: res.data.user_id, data: res })
      .catch((err) => {
        console.log('db write failed', err)
      })
  },
  (error) => {
    // 这里为什么那么做
    // Framework中通过privilege version是否为null判断是否展示页面整体
    // 会存在用户已登录但未激活账号，code === 303，仍需进入到系统，TODO：待产品优化，未激活理应跳到单独的静态页
    // 但是这里仍需要保证用户未登录不允许看到页面内容
    let userNotLogin = false
    try {
      userNotLogin = error.message.startsWith('custom exception 255')
    } catch (err) {}

    pageUserSubject.next({
      data: {
        privilege: {
          version: userNotLogin ? null : 9,
          user: {},
          list: [],
          scope_type: 0,
        },
        setting: {},
      },
    })
  },
)

db.table('pageUser')
  .get({ id: Number(cookie.get('userId')) })
  .then((res) => {
    if (res && res.data) {
      pageUserSubject.next(res.data)
    }
  })
  .catch((error) => {
    console.error('get pageUser in db', error)
  })

const renderApp: render = ({ appContent, loading }) => {
  masterApp.$data.content = appContent
  masterApp.$data.loading = loading

  return masterApp.$nextTick()
}

const galioSubApp = {
  // SUB_APP__TALK: ASSETS.TALK,
}

getSubAppsConfig().then((appsConfig) => {
  const apps: RegistrableApp<AppProps>[] = appsConfig.map((config) => {
    return {
      name: config.name,
      activeRule: config.activeRule,
      render: renderApp,
      entry: getAppEntry(config.assets.app, config.name),
      props: {
        pageUser$,
        pageUserSubject,
        config: config,
        precache: async () => {
          const manifest = config.assets.manifest

          if (sw && config.precache !== 'none' && manifest) {
            const entryAssets = config.assets.app.css.concat(config.assets.app.js)

            return sw.precache({
              projectName: config.assets.metadata.build.projectName,
              manifest:
                config.precache === 'entry' ? manifest.filter(({ url }) => entryAssets.includes(url)) : manifest,
            })
          }
        },
      },
    }
  })

  const lifeCycles = getLifeCycles(masterAppMounted)

  registerMicroApps(apps, lifeCycles, {
    fetch: (url: RequestInfo | URL) => {
      const fetch = createRepeatFetch(sourceFetch, {
        max: 3,
        onRepeat(args, curCount) {
          if (curCount === 3) {
            const target = toMainDomain(args[0] as string)
            addRetrySentryBreadcrumb('qk', {
              url: args[0] as string,
              target,
              curCount,
            })
            return [target, args[1]]
          }
          addRetrySentryBreadcrumb('qk', {
            url: args[0] as string,
            curCount,
          })
          return args
        },
        onFinally({ isSuccess, count, messages, finallyUrl }) {
          if (count === 1 && isSuccess) {
            return
          }
          if (url === finallyUrl) {
            reportRetry(isSuccess ? 'retry-qk-repeat-success' : 'retry-qk-repeat-fail', { url, messages, count })
          } else {
            reportRetry(isSuccess ? 'retry-qk-last-success' : 'retry-qk-last-fail', {
              url,
              target: finallyUrl,
              messages,
              count,
            })
          }
        },
      })
      return fetch(url as string, {
        mode: 'cors',
        credentials: 'same-origin',
      })
    },
  })

  setDefaultMountApp(shellConfig.defaultMountApp)

  start({
    prefetch: false,
    jsSandbox: false,
    singular: false,
  })

  if (process.env.NODE_ENV === 'production') {
    tabOnly('checkNewVersion', () => {
      console.info('[tabOnly-checkNewVersion]: check bootstrap.')
      checkNewVersion(appsConfig)
    })
  }
})

requestPermission() // 请求 notification 权限
